/**
 * Per-TOKEN metadata cache, with the batch demoted to a transport detail.
 *
 * The same shape as `addressOverlayRefCache`, for the same reason and against
 * the same failure. A token definition is cached under `(chain, token)`, and
 * the `queryFn` does not issue a request — it enqueues the ref here.
 * Everything enqueued within one tick is coalesced into one
 * `POST /tokens/batch`, and each waiter is handed its own slice.
 *
 * Caching under the ref SET instead would have been the obvious thing and is
 * the thing that went wrong for addresses: the live on-chain feed's graph grows
 * by a block every few seconds, so a set key re-reads every token in it each
 * time one new one appears. Per ref, a graph that gains a token costs a token.
 *
 * Two properties this buys, which are the point rather than a side effect:
 *
 * - A token already in cache never reaches its `queryFn`, so it is not in the
 *   request body either. Across a session, the second graph asks only about
 *   what the first one did not contain.
 * - The tracer page's canvas and its hop ladder read the same tokens. They
 *   share entries rather than each holding a set, so their overlap costs one
 *   read between them.
 *
 * A token definition is about as cacheable as data gets — decimals and a ticker
 * are immutable facts about a deployed contract — so the stale time here is
 * long on purpose. What it is NOT is a price: `price_usd` is always null on
 * these rows (see `@authe/fe-commons/tokens`), so nothing cached here can go
 * stale in a way that misleads.
 *
 * The per-query `AbortSignal` is deliberately not threaded into the shared
 * request: one unmounting observer must not cancel a batch the others are still
 * waiting on, and the answer is wanted in the cache either way.
 */
import type { ApiClient } from "@authe/fe-commons/api";
import {
  fetchTokenMetadata,
  tokenMetadataKey,
  type TokenMetadata,
  type TokenMetadataRef,
} from "@authe/fe-commons/tokens";

/**
 * One token's answer.
 *
 * `metadata: null` is a real answer and a different one from "not fetched
 * yet": it means the service has no definition for this contract. The surface
 * then says the scale is unknown rather than scaling by 18 — which for a
 * 6-decimal token is wrong by a factor of a trillion, in the direction that
 * makes a theft look like dust.
 */
export interface TokenMetadataRefEntry {
  ref: TokenMetadataRef;
  metadata: TokenMetadata | null;
}

/** How long a fetched definition stays fresh. Decimals do not change. */
export const TOKEN_METADATA_STALE_MS = 60 * 60 * 1000;

/** The key ONE token is cached under, folded so `0x1` and `eip155:1` are one entry. */
export function tokenMetadataQueryKey(ref: TokenMetadataRef): string[] {
  return ["token-metadata-ref", tokenMetadataKey(ref.chainId, ref.address)];
}

// ── the coalescer ───────────────────────────────────────────────────────────

interface Waiter {
  ref: TokenMetadataRef;
  resolve: (entry: TokenMetadataRefEntry) => void;
  reject: (error: unknown) => void;
}

interface Group {
  client: ApiClient;
  waiters: Waiter[];
}

/**
 * Client identity in the group key, so two `ApiClient`s in one process (tests,
 * a second provider) can never have their refs folded into one request. A
 * WeakMap rather than a field, because the client is not ours to stamp.
 */
const clientIds = new WeakMap<object, number>();
let nextClientId = 0;
function clientId(client: ApiClient): string {
  const key = client as unknown as object;
  let id = clientIds.get(key);
  if (id === undefined) {
    id = nextClientId += 1;
    clientIds.set(key, id);
  }
  return String(id);
}

const groups = new Map<string, Group>();
let scheduled: ReturnType<typeof setTimeout> | null = null;

/**
 * Enqueue one token and get back its own answer.
 *
 * This is the whole `queryFn` of a per-token query. The flush is a macrotask
 * rather than a microtask for the reason the address queue documents: TanStack
 * starts every query in a `useQueries` set within one tick, and a macrotask
 * window is what reliably catches all of them — including the ones a sibling
 * hook mounted in the same commit.
 */
export function enqueueTokenMetadataRef(
  ref: TokenMetadataRef,
  client: ApiClient,
): Promise<TokenMetadataRefEntry> {
  return new Promise<TokenMetadataRefEntry>((resolve, reject) => {
    const key = clientId(client);
    const group = groups.get(key);
    if (group) group.waiters.push({ ref, resolve, reject });
    else groups.set(key, { client, waiters: [{ ref, resolve, reject }] });
    if (scheduled === null) scheduled = setTimeout(flush, 0);
  });
}

function flush(): void {
  scheduled = null;
  const pending = [...groups.values()];
  groups.clear();
  for (const group of pending) void runGroup(group);
}

async function runGroup(group: Group): Promise<void> {
  // Several observers can want the SAME token in one tick (the canvas and the
  // ladder below it drawing one contract). They are one key in the request and
  // each gets the answer.
  const byKey = new Map<string, Waiter[]>();
  for (const waiter of group.waiters) {
    const key = tokenMetadataKey(waiter.ref.chainId, waiter.ref.address);
    const existing = byKey.get(key);
    if (existing) existing.push(waiter);
    else byKey.set(key, [waiter]);
  }
  const refs = [...byKey.values()].map((waiters) => waiters[0]!.ref);

  try {
    // Chunking to the endpoint's own 100-key cap lives in `fetchTokenMetadata`;
    // this hands it the whole window.
    const rows = await fetchTokenMetadata(group.client, refs);
    const found = new Map<string, TokenMetadata>();
    for (const row of rows) found.set(tokenMetadataKey(row.chainId, row.address), row);
    for (const [key, waiters] of byKey) {
      // A key the batch answered nothing for resolves to `null`, not a reject:
      // "we have no definition for this token" is an answer, and caching it is
      // what keeps a graph full of unknown spam contracts from asking again on
      // every render.
      const entry: TokenMetadataRefEntry = {
        ref: waiters[0]!.ref,
        metadata: found.get(key) ?? null,
      };
      for (const waiter of waiters) waiter.resolve(entry);
    }
  } catch (error) {
    // Every waiter hears it and retries on its own query's policy, which
    // re-coalesces into one request rather than N.
    for (const waiters of byKey.values()) for (const waiter of waiters) waiter.reject(error);
  }
}

/** Test seam: drop anything queued but not yet flushed. */
export function __resetTokenMetadataQueue(): void {
  if (scheduled !== null) clearTimeout(scheduled);
  scheduled = null;
  groups.clear();
}
